<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { ref, computed, getCurrentInstance } from 'vue';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputNumber from 'primevue/inputnumber';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

defineOptions({
    layout: AdminMainLayout,
});

const { exchanges, paymentTaxes: initialPaymentTaxes, globalFixedValue: initialGlobalFixedValue, globalFixedCurrency: initialGlobalFixedCurrency, bankAccounts, wiseBankAccountId: initialWiseBankAccountId } = defineProps({
    exchanges: Array,
    paymentTaxes: Object,
    globalFixedValue: Number,
    globalFixedCurrency: String,
    bankAccounts: Array,
    wiseBankAccountId: Number,
});

// Local state for editing
const editingRows = ref({});
const isEditing = ref(false);

const paymentTaxes = ref({
    stripe: initialPaymentTaxes?.stripe ?? 0,
    pix:    initialPaymentTaxes?.pix    ?? 0,
    boleto: initialPaymentTaxes?.boleto ?? 0,
    wise:   initialPaymentTaxes?.wise   ?? 0,
});
const loading = ref(false);
const wiseBankAccountId = ref(initialWiseBankAccountId ?? null);
const wiseBankLoading = ref(false);

const bankAccountOptions = computed(() =>
    (bankAccounts ?? []).map(b => ({
        label: `${b.name} — ${b.bank_name ?? ''} ${b.iban ? '(IBAN: ' + b.iban + ')' : ''}`.trim(),
        value: b.id,
    }))
);

const saveWiseBankAccount = () => {
    if (! wiseBankAccountId.value) return;
    const form = useForm({ bank_account_id: wiseBankAccountId.value });
    form.put(route('admin.wise-bank-account.update'), {
        onStart: () => { wiseBankLoading.value = true; },
        onFinish: () => { wiseBankLoading.value = false; },
        onSuccess: () => {
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Wise bank account updated successfully'), life: 3000 });
        },
        onError: () => {
            toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to update Wise bank account'), life: 4000 });
        },
    });
};

const METHOD_LABELS = {
    stripe: { label: 'Stripe (Cartão)', icon: 'pi-credit-card', iconClass: 'text-indigo-500' },
    pix:    { label: 'Pix',             icon: 'pi-qrcode',      iconClass: 'text-green-500'  },
    boleto: { label: 'Boleto',          icon: 'pi-barcode',     iconClass: 'text-orange-500' },
    wise:   { label: 'Wise',            icon: 'pi-send',        iconClass: 'text-blue-500'   },
};

// Global fixed addition configuration
const globalFixedValue = ref(initialGlobalFixedValue || 675.00);
const selectedCurrency = ref(initialGlobalFixedCurrency || 'BRL');

// Calculate converted fixed addition for any currency
const getConvertedFixedAddition = (targetCurrency, targetRate) => {
    if (targetCurrency === selectedCurrency.value) {
        return Math.round(globalFixedValue.value * 100); // Convert to centavos
    }
    
    // Find the exchange rate for the selected currency
    const selectedExchange = exchanges.find(e => e.currency === selectedCurrency.value);
    if (!selectedExchange) return 0;
    
    // Convert from selected currency to target currency
    // First convert to USD (base), then to target
    const valueInUSD = globalFixedValue.value / selectedExchange.value;
    const valueInTargetCurrency = valueInUSD * targetRate;
    
    return Math.round(valueInTargetCurrency * 100); // Convert to centavos
};

// Currency options for the select
const currencyOptions = computed(() => {
    return exchanges.map(exchange => ({
        label: exchange.currency,
        value: exchange.currency
    }));
});

// Form for batch updates
const form = useForm({
    updates: []
});


// Save global configuration
const saveGlobalConfig = () => {
    const globalForm = useForm({
        value: globalFixedValue.value,
        currency: selectedCurrency.value
    });
    
    globalForm.put(route('admin.global-fixed.update'), {
        onStart: () => showLoading('Updating global fixed addition...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Global fixed addition updated successfully'),
                life: 3000
            });
        },
        onError: (errors) => {
            console.error('Global fixed addition errors:', errors);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update global fixed addition'),
                life: 4000
            });
        }
    });
};


// Check if any row is being edited
const hasActiveEdits = computed(() => {
    return Object.keys(editingRows.value).length > 0;
});

// Format currency display
const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value / 100 || 0);
};


// Get currency pair display
const getCurrencyPair = (exchange) => {
    return `${exchange.currency} Rate`;
};

const savePaymentTaxes = () => {
    for (const method of ['stripe', 'pix', 'boleto', 'wise']) {
        const v = Number(paymentTaxes.value[method]);
        if (isNaN(v) || v < 0 || v > 100) {
            toast.add({
                severity: 'error',
                summary: $t('Invalid Value'),
                detail: $t('All taxes must be between 0% and 100%'),
                life: 4000
            });
            return;
        }
    }

    const form = useForm({
        payment_tax_stripe: paymentTaxes.value.stripe,
        payment_tax_pix:    paymentTaxes.value.pix,
        payment_tax_boleto: paymentTaxes.value.boleto,
        payment_tax_wise:   paymentTaxes.value.wise,
    });

    form.put(route('admin.payment-tax.update'), {
        onStart: () => { loading.value = true; showLoading('Updating payment taxes...'); },
        onFinish: () => { loading.value = false; hideLoading(); },
        onSuccess: () => {
            toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Payment taxes updated successfully'), life: 3000 });
        },
        onError: () => {
            toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to update payment taxes'), life: 4000 });
        },
    });
};
</script>

<template>
    
    <div class="grid">
        <div class="col-12">
            <Card>
                <template #title>
                    <div class="flex items-center mb-6">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-cog text-2xl"></i>
                            <span>{{ $t('Pricing Configuration') }}</span>
                        </div>
                    </div>
                </template>

                <template #content>
                    <!-- Payment Tax Section -->
                    <div class="mb-6">
                        <Card>
                            <template #title>
                                <div class="flex items-center gap-3">
                                    <i class="pi pi-credit-card text-orange-500"></i>
                                    <span>{{ $t('Payment Processing Tax by Method') }}</span>
                                </div>
                            </template>

                            <template #content>
                                <p class="text-sm text-gray-600 mb-4">
                                    {{ $t('Configure the tax percentage applied per payment method.') }}
                                </p>

                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                    <div
                                        v-for="method in ['stripe', 'pix', 'boleto', 'wise']"
                                        :key="method"
                                        class="flex items-center gap-3 p-3 border border-surface-200 rounded-lg"
                                    >
                                        <i :class="`pi ${METHOD_LABELS[method].icon} text-xl ${METHOD_LABELS[method].iconClass}`"></i>
                                        <span class="flex-1 font-medium text-sm">{{ METHOD_LABELS[method].label }}</span>
                                        <InputNumber
                                            v-model="paymentTaxes[method]"
                                            :min="0"
                                            :max="100"
                                            :maxFractionDigits="2"
                                            :step="0.5"
                                            size="small"
                                            :inputStyle="{ width: '5rem' }"
                                            :disabled="loading"
                                        />
                                        <span class="text-gray-500 text-sm">%</span>
                                    </div>
                                </div>

                                <div class="flex gap-2">
                                    <Button
                                        :label="$t('Save')"
                                        icon="pi pi-save"
                                        size="small"
                                        severity="success"
                                        @click="savePaymentTaxes"
                                        :loading="loading"
                                        :disabled="loading"
                                    />
                                </div>

                                <!-- Example calculation -->
                                <div class="mt-4 p-3 bg-gray-50 rounded text-sm">
                                    <div class="font-semibold text-gray-700 mb-1">{{ $t('Example (Stripe)') }}:</div>
                                    <div class="text-gray-600">
                                        {{ $t('Order total') }}: $100.00 → {{ $t('With tax') }}: ${{ (100 * (1 + paymentTaxes.stripe / 100)).toFixed(2) }}
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </div>

                    <!-- Wise Default Bank Account Section -->
                    <div class="mb-6">
                        <Card>
                            <template #title>
                                <div class="flex items-center gap-3">
                                    <i class="pi pi-send text-blue-500"></i>
                                    <span>{{ $t('Wise — Default Bank Account') }}</span>
                                </div>
                            </template>
                            <template #content>
                                <p class="text-sm text-gray-600 mb-4">
                                    {{ $t('Select which bank account will be shown to clients when paying via Wise transfer.') }}
                                </p>
                                <div class="flex items-center gap-3">
                                    <Select
                                        v-model="wiseBankAccountId"
                                        :options="bankAccountOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        :placeholder="$t('Select a bank account')"
                                        class="flex-1"
                                        :disabled="wiseBankLoading"
                                    />
                                    <Button
                                        :label="$t('Save')"
                                        icon="pi pi-save"
                                        size="small"
                                        severity="success"
                                        @click="saveWiseBankAccount"
                                        :loading="wiseBankLoading"
                                        :disabled="wiseBankLoading || !wiseBankAccountId"
                                    />
                                </div>
                                <p v-if="!bankAccounts?.length" class="text-sm text-orange-500 mt-2">
                                    <i class="pi pi-exclamation-triangle mr-1"></i>
                                    {{ $t('No active bank accounts found. Please create one first.') }}
                                </p>
                            </template>
                        </Card>
                    </div>

                    <!-- Global Fixed Addition Section -->
                    <div class="mb-6">
                        <Card>
                            <template #title>
                                <div class="flex items-center gap-3">
                                    <i class="pi pi-globe text-blue-500"></i>
                                    <span>{{ $t('Global Fixed Addition') }}</span>
                                </div>
                            </template>
                            
                            <template #content>
                                <div class="mb-4">
                                    <div class="bg-gray-50 p-4 border-l-4 border-gray-400 rounded">
                                        <div class="flex items-center gap-10">
                                            <div class="flex items-center gap-3 mr-10">
                                                <label class="text-sm font-medium text-gray-700">
                                                    {{ $t('Amount') }}:
                                                </label>

                                                <InputNumber
                                                    v-model="globalFixedValue"
                                                    :minFractionDigits="2"
                                                    :maxFractionDigits="2"
                                                    class="w-32"
                                                    size="small"
                                                />
                                            </div>

                                            <div class="flex items-center gap-3">
                                                <Select
                                                    v-model="selectedCurrency"
                                                    :options="currencyOptions"
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    class=""
                                                />
                                            </div>

                                            <div class="flex items-center">
                                                <Button
                                                    :label="$t('Save')"
                                                    icon="pi pi-save"
                                                    size="small"
                                                    severity="success"
                                                    @click="saveGlobalConfig"
                                                    :disabled="loading"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </div>

                    <!-- Formula Configuration Section -->
                    <div class="mb-4">
                        <div class="flex align-items-center gap-2 mb-3">
                            <i class="pi pi-calculator text-lg"></i>
                            <h3 class="text-lg font-semibold m-0">{{ $t('Exchange Rate Formula Configuration') }}</h3>
                        </div>
                    </div>

                    <DataTable 
                        :value="exchanges" 
                        responsiveLayout="scroll"
                        class="p-datatable-sm"
                        :scrollable="true"
                        scrollHeight="600px"
                    >
                        <template #empty>
                            <div class="text-center p-4">
                                {{ $t('No exchange rates configured') }}
                            </div>
                        </template>

                        <Column field="currency_pair" :header="$t('Exchange Rate')" style="min-width: 120px">
                            <template #body="slotProps">
                                <div class="flex align-items-center gap-2">
                                    <span class="font-semibold">{{ getCurrencyPair(slotProps.data) }}</span>
                                    <span class="text-sm text-gray-600">
                                        ({{ (slotProps.data.value / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }})
                                    </span>

                                </div>
                            </template>
                        </Column>

                        <Column field="fixed_addition" :header="$t('Fixed Addition')" style="min-width: 150px">
                            <template #body="slotProps">
                                <div class="flex align-items-center flex-column align-items-start">
                                    <span class="font-semibold">{{ formatCurrency(getConvertedFixedAddition(slotProps.data.currency, slotProps.data.value), slotProps.data.currency) }}</span>
                                </div>
                            </template>
                        </Column>


                        <Column field="updated_at" :header="$t('Last Updated')" style="min-width: 120px">
                            <template #body="slotProps">
                                <span class="text-sm text-gray-600">
                                    {{ slotProps.data.updated_at ? new Date(slotProps.data.updated_at).toLocaleDateString() : '-' }}
                                </span>
                            </template>
                        </Column>
                    </DataTable>

                    <div v-if="hasActiveEdits" class="mt-4 p-3 bg-yellow-50 border-left-3 border-yellow-500">
                        <p class="text-sm text-yellow-700 m-0">
                            <i class="pi pi-info-circle mr-2"></i>
                            {{ $t('You have unsaved changes. Please save or cancel your edits.') }}
                        </p>
                    </div>
                </template>
            </Card>
        </div>
    </div>
</template>

<style scoped>
.border-left-3 {
    border-left-width: 3px;
    border-left-style: solid;
}

:deep(.p-inputnumber-input) {
    font-size: 0.875rem;
}
</style>